import React, { useEffect, useState, useRef } from 'react';
import { PitchDeck } from './pages/PitchDeck';
import { AdvantageSummary } from './pages/AdvantageSummary';
import { PricingProvider } from './contexts/PricingContext';
import { SystemPage } from './pages/SystemPage';
import { D2DLaunchProposal } from './pages/D2DLaunchProposal';
import { FinancialModelPage } from './pages/FinancialModelPage';
import { LineChart, LayoutGrid, Lock, Rocket, Menu } from 'lucide-react';
type ViewType = 'deck' | 'advantage' | 'system' | 'd2dlaunch' | 'financial';
export function App() {
  // ALL hooks at the top
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [view, setView] = useState<ViewType>('deck');
  const [menuOpen, setMenuOpen] = useState(false);
  // Scroll position management
  const scrollPositions = useRef<Record<string, number>>({});
  // Check auth on mount
  useEffect(() => {
    setIsClient(true);
    const authStatus = localStorage.getItem('site_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  // Restore scroll position when view changes
  useEffect(() => {
    // Small timeout to ensure DOM is ready
    const timer = setTimeout(() => {
      window.scrollTo(0, scrollPositions.current[view] || 0);
    }, 0);
    return () => clearTimeout(timer);
  }, [view]);
  // Handler functions
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'HIRE100k' || password === 'TheManny') {
      localStorage.setItem('site_authenticated', 'true');
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };
  const handleViewChange = (newView: ViewType) => {
    // Save current scroll position before switching
    scrollPositions.current[view] = window.scrollY;
    setView(newView);
  };
  // Show loading during initial mount
  if (!isClient) {
    return <div className="min-h-screen bg-[#0A0F1C]" data-id="element-0" data-mp="38b66v0ldsdg"></div>;
  }
  // Show login if not authenticated
  if (!isAuthenticated) {
    return <div className="min-h-screen w-full bg-[#0A0F1C] flex items-center justify-center p-4 sm:p-6" data-id="element-1" data-mp="d4xobc1ja5bs">
        <div className="w-full max-w-md" data-id="element-2" data-mp="85bd1859mz5m">
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl" data-id="element-3" data-mp="86cqfj9r7xqi">
            <div className="flex justify-center mb-6" data-id="element-4" data-mp="3bmbdpnwu87jc">
              <div className="w-16 h-16 rounded-full bg-[#7FE0C3]/10 border border-[#7FE0C3]/20 flex items-center justify-center" data-id="element-5" data-mp="3bnmedeuzkz68">
                <Lock className="w-8 h-8 text-[#7FE0C3]" data-id="element-6" data-mp="3qd0rmy1oo0qw" />
              </div>
            </div>

            <h1 className="text-xl sm:text-2xl font-bold text-white text-center mb-2" data-id="element-7" data-mp="3liefgsns245r">
              Welcome to D2D HIRE
            </h1>
            <p className="text-sm sm:text-base text-slate-400 text-center mb-8" data-id="element-8" data-mp="2swbtpffutims">
              Please enter your access code to continue
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-9" data-mp="18boj8j2po5vd">
              <div data-id="element-10" data-mp="1d5j8s34zzf2d">
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-11" data-mp="1d5sqz4jswhxi">
                  Access Code
                </label>
                <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#7FE0C3] focus:border-transparent transition-all text-base" placeholder="Enter access code" autoFocus data-id="element-12" data-mp="13ey7g7xxvwfr" />
              </div>

              {error && <div className="text-red-400 text-sm text-center bg-red-500/10 border border-red-500/20 rounded-lg py-2" data-id="element-13" data-mp="ar28sxot7ugc">
                  {error}
                </div>}

              <button type="submit" className="w-full px-4 py-3 bg-[#7FE0C3] hover:bg-[#5ACCA8] text-[#163A5F] font-medium rounded-lg transition-all transform active:scale-95" data-id="element-14" data-mp="m65k6vdaka91">
                Access Site
              </button>
            </form>
          </div>
        </div>
      </div>;
  }
  // Main content
  return <PricingProvider data-id="element-15" data-mp="14wx1qk7fn6yi">
    <div className="relative" data-id="element-16" data-mp="14wexb0am9mfi">
      {view === 'deck' && <PitchDeck data-id="element-17" data-mp="14xpxyr8rme2e" />}
      {view === 'advantage' && <AdvantageSummary data-id="element-18" data-mp="zxao49nozop6" />}
      {view === 'system' && <SystemPage data-id="element-19" data-mp="zw0mxbljm0eb" />}
      {view === 'd2dlaunch' && <D2DLaunchProposal data-id="element-20" data-mp="vwibd6fm31o5" />}
      {view === 'financial' && <FinancialModelPage data-id="element-21" data-mp="vvyw5xhcirdt" />}

      {/* Left Side Menu */}
      <div className="no-print fixed top-3 left-3 sm:top-6 sm:left-6 z-[100] group" onMouseLeave={() => setMenuOpen(false)} data-id="element-22" data-mp="3kob4sgya686c">
        <button type="button" onMouseEnter={() => setMenuOpen(true)} onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-label="Open navigation menu" className="px-3 py-2 sm:px-4 sm:py-2 bg-slate-900/90 backdrop-blur-sm border border-slate-700 rounded-full shadow-xl text-slate-400 hover:text-white hover:border-[#7FE0C3]/50 transition-colors duration-150 ease-out flex items-center justify-center gap-2 text-xs sm:text-sm font-medium min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-23" data-mp="3gok6ls6fcprs">
          <Menu className="w-4 h-4" data-id="element-24" data-mp="2jw3x689yc864" />
          <span className="hidden sm:inline" data-id="element-25" data-mp="2nv2yzk0dzotc">Menu</span>
        </button>

        <div className={`absolute top-full left-0 pt-2 w-52 origin-top-left transition-[opacity,transform] duration-150 ease-out ${menuOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`} data-id="element-26" data-mp="2supek03oth5b">
          <div className="bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-xl shadow-2xl p-1.5 space-y-0.5" data-id="element-27" data-mp="yhg2q1no5jjw">
            {[{
              id: 'deck' as ViewType,
              label: 'Deck',
              icon: LayoutGrid
            }, {
              id: 'd2dlaunch' as ViewType,
              label: 'D2D Launch',
              icon: Rocket
            }, {
              id: 'financial' as ViewType,
              label: 'Financial Model',
              icon: LineChart
            }].map(item => <button key={item.id} onClick={() => {
              handleViewChange(item.id);
              setMenuOpen(false);
            }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ease-out ${view === item.id ? 'bg-[#7FE0C3] text-[#163A5F]' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`} data-id="element-28" data-mp="2frd47cwn88dx">
                <item.icon className="w-4 h-4 shrink-0" data-id="element-29" data-mp="2yj5z5cv9w3oa" />
                {item.label}
              </button>)}
          </div>
        </div>
      </div>

    </div>
    </PricingProvider>;
}